import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Download, Music } from 'lucide-react';
import { SoundTrack } from '../../types';

interface AudioPlayerProps {
  track: SoundTrack;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ track }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
      }
    };
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const categoryLabel = track.category === 'music'
    ? t('art.music.categories.music')
    : t('art.music.categories.soundDesign');

  return (
    <div className="art-card rounded-2xl p-4 sm:p-5 group hover:border-brand-amber/30 transition-all">
      <audio
        ref={audioRef}
        src={track.fileUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />

      <div className="flex items-start gap-3 sm:gap-4">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="shrink-0 w-12 h-12 rounded-xl bg-brand-amber/10 hover:bg-brand-amber/20 border border-brand-amber/20 flex items-center justify-center text-brand-amber transition-all hover:scale-105 active:scale-95"
          aria-label={isPlaying ? t('art.music.pause') : t('art.music.play')}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        {/* Track Info & Progress */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h4 className="font-bold text-slate-900 dark:text-white truncate text-sm sm:text-base">
                {track.title}
              </h4>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-amber/10 border border-brand-amber/20 rounded-full text-[10px] uppercase font-bold text-brand-amber">
                  <Music className="w-2.5 h-2.5" />
                  {categoryLabel}
                </span>
                <span className="text-xs text-slate-500">{track.date}</span>
              </div>
            </div>

            {/* Download Button */}
            <a
              href={track.fileUrl}
              download
              className="shrink-0 p-2 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-brand-amber/10 border border-black/10 dark:border-white/10 hover:border-brand-amber/20 text-slate-500 hover:text-brand-amber transition-all"
              title={t('art.music.download')}
            >
              <Download className="w-4 h-4" />
            </a>
          </div>

          {/* Progress Bar */}
          <div
            className="relative w-full h-1.5 bg-black/10 dark:bg-white/10 rounded-full cursor-pointer group/bar"
            onClick={handleSeek}
          >
            <div
              className="absolute inset-y-0 left-0 bg-brand-amber rounded-full transition-[width] duration-100"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-brand-amber rounded-full shadow-md opacity-0 group-hover/bar:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>

          {/* Time */}
          <div className="flex justify-between text-[11px] text-slate-500 font-mono">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      {/* Description */}
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-16">
          {t(`sounds.${track.id}.description`)}
        </p>
    </div>
  );
};

export default AudioPlayer;
